import axios from 'axios';

const base = process.env.REACT_APP_BACK_URL || `${window.location.protocol}//${window.location.hostname}:5000`;

export type EmprendedorMeta = {
  tipo: 'emprendedor';
  nombreMarca: string;
  rubro: string;
  telefono: string;
  whatsapp?: string;
  bio?: string;
  estado?: 'informal' | 'en_formalizacion' | 'formalizado';
};

export type ComercioStore = {
  comercio_id: number;
  socio_id: number;
  nombre: string;
  slug: string;
  descripcion?: string;
  logo_url?: string;
  activo: boolean;
  emprendedor?: EmprendedorMeta | null;
  es_emprendedor?: boolean;
};

export type ProductoStore = {
  producto_id: number;
  comercio_id: number;
  nombre: string;
  descripcion?: string;
  precio: number;
  stock: number;
  imagen_url?: string;
  activo: boolean;
};

export const getComerciosMarket = async () => {
  const { data } = await axios.get(`${base}/market/comercios`);
  return data as ComercioStore[];
};

export const getComercioBySlug = async (slug: string) => {
  const { data } = await axios.get(`${base}/market/comercios/${slug}`);
  return data as ComercioStore;
};

export const getProductosMarket = async (comercioId?: number, includeInactivos = false) => {
  const params = new URLSearchParams();
  if (comercioId) params.set('comercio_id', String(comercioId));
  if (includeInactivos) params.set('include_inactivos', '1');
  const query = params.toString();
  const url = `${base}/market/productos${query ? `?${query}` : ''}`;
  const { data } = await axios.get(url);
  return data as ProductoStore[];
};

export const createComercioMarket = async (payload: {
  nombre: string;
  socio_id: number;
  descripcion?: string;
  logo_url?: string;
  activo?: boolean;
}) => {
  const { data } = await axios.post(`${base}/market/comercios`, payload);
  return data as ComercioStore;
};

export const createProductoMarket = async (payload: {
  comercio_id: number;
  nombre: string;
  descripcion?: string;
  precio?: number;
  stock?: number;
  imagen_url?: string;
  activo?: boolean;
}) => {
  const { data } = await axios.post(`${base}/market/productos`, payload);
  return data as ProductoStore;
};

export const updateProductoMarket = async (productoId: number, payload: Partial<{
  nombre: string;
  descripcion: string;
  precio: number;
  stock: number;
  imagen_url: string;
  activo: boolean;
}>) => {
  const { data } = await axios.put(`${base}/market/productos/${productoId}`, payload);
  return data as ProductoStore;
};

export const deleteProductoMarket = async (productoId: number) => {
  const { data } = await axios.delete(`${base}/market/productos/${productoId}`);
  return data as { ok: boolean; producto_id: number; activo: boolean };
};

export const updateComercioMarket = async (
  comercioId: number,
  payload: Partial<{ nombre: string; descripcion: string; logo_url: string; activo: boolean; slug: string }>
) => {
  const { data } = await axios.put(`${base}/market/comercios/${comercioId}`, payload);
  return data as ComercioStore;
};

export const deleteComercioMarket = async (comercioId: number) => {
  const { data } = await axios.delete(`${base}/market/comercios/${comercioId}`);
  return data as { ok: boolean; comercio_id: number; activo: boolean };
};

export const getComerciosAdminMarket = async (includeInactivos = true) => {
  const params = new URLSearchParams();
  if (includeInactivos) params.set('include_inactivos', '1');
  const { data } = await axios.get(`${base}/market/admin/comercios?${params.toString()}`);
  return data as Array<ComercioStore & { productos_total?: number; productos_activos?: number; productos_inactivos?: number }>;
};

export const getEmprendedoresAdminMarket = async (includeInactivos = true) => {
  const params = new URLSearchParams();
  if (includeInactivos) params.set('include_inactivos', '1');
  const { data } = await axios.get(`${base}/market/admin/emprendedores?${params.toString()}`);
  return data as ComercioStore[];
};

export const resetPasswordEmprendedorAdminMarket = async (comercioId: number, password: string) => {
  const { data } = await axios.post(`${base}/market/admin/emprendedores/${comercioId}/reset-password`, { password });
  return data as { ok: boolean; comercio_id: number; email: string };
};

export const deleteEmprendedorAdminMarket = async (comercioId: number) => {
  const { data } = await axios.delete(`${base}/market/admin/emprendedores/${comercioId}`);
  return data as { ok: boolean; comercio_id: number; activo: boolean };
};

export const subirImagenProductoMarket = async (productoId: number, file: File, token: string) => {
  const form = new FormData();
  form.append('file', file);
  const { data } = await axios.post(`${base}/files/productos/${productoId}/imagen`, form, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
  });
  return data as { productoId: number; imagen_url: string };
};

export const registrarEmprendedor = async (payload: {
  nombre_marca: string;
  rubro: string;
  telefono: string;
  whatsapp?: string;
  bio?: string;
  email: string;
  password: string;
}) => {
  const { data } = await axios.post(`${base}/market/emprendedores/registro`, payload);
  return data as {
    comercio: ComercioStore;
    token: string | null;
    pendiente_aprobacion?: boolean;
    message?: string;
  };
};

export const loginEmprendedor = async (payload: { email: string; password: string }) => {
  const { data } = await axios.post(`${base}/market/emprendedores/login`, payload);
  return data as { comercio: ComercioStore; token: string };
};

export const getMiCuentaEmprendedor = async (token: string) => {
  const { data } = await axios.get(`${base}/market/emprendedores/mi-cuenta`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data as { comercio: ComercioStore; productos: ProductoStore[] };
};

export const updateMiCuentaEmprendedor = async (
  token: string,
  payload: Partial<{ nombre_marca: string; rubro: string; telefono: string; whatsapp: string; bio: string; estado: string; acompanamiento: any }>
) => {
  const { data } = await axios.put(`${base}/market/emprendedores/mi-cuenta`, payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data as ComercioStore;
};

export const sugerirProductoIaEmprendedor = async (payload: {
  nombre_marca: string;
  rubro: string;
  foto_nombre?: string;
  imageFile?: File | null;
}) => {
  let data: any;
  if (payload.imageFile) {
    const form = new FormData();
    form.append('image', payload.imageFile);
    form.append('nombre_marca', payload.nombre_marca || 'Emprendedor');
    form.append('rubro', payload.rubro || 'general');
    if (payload.foto_nombre) form.append('foto_nombre', payload.foto_nombre);
    const resp = await axios.post(`${base}/market/emprendedores/ia-sugerir`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    data = resp.data;
  } else {
    const resp = await axios.post(`${base}/market/emprendedores/ia-sugerir`, payload);
    data = resp.data;
  }

  return data as {
    titulo: string;
    descripcion: string;
    precio_sugerido: number;
    categoria: string;
    confianza: number;
    ocr?: { linesCount: number; lines: string[] };
  };
};

export const publicarProductoEmprendedor = async (payload: {
  comercio_id: number;
  nombre: string;
  descripcion?: string;
  precio?: number;
  stock?: number;
  imagen_url?: string;
}) => {
  const { data } = await axios.post(`${base}/market/emprendedores/publicar-producto`, payload);
  return data as ProductoStore;
};
